import React, { useState, useMemo } from "react"
import loadable from "@loadable/component"
import "leaflet/dist/leaflet.css"

const LeafletGHGMap = loadable(() => import("../../components/LeafletGHGMap"), {
  ssr: false,
})

const years = [2025]
const dataRoot = "/downloads/ghg/EN"
const version = "v20260520"

const sites = [
  ["SNJ", "Stockholm, NJ", "Earth Networks, Inc.", 41.143556, 74.538718, 406, 42, 53],
  ["LEW", "Lewisburg, PA", "Earth Networks, Inc.", 40.944646, 76.878855, 166, 50, 95],
  ["MNY", "Mineola, NY", "Earth Networks, Inc.", 40.749507, 73.63838, 34, 50, 62],
  ["MSH", "Mashpee, MA", "Earth Networks, Inc.", 41.656694, 70.4975, 32, 25, 46],
  ["HCT", "Hamden, CT", "Earth Networks, Inc.", 41.433705, 72.945224, 204, 55, 115],
  ["DNH", "Canaan, NH", "Earth Networks, Inc.", 43.7088876, 72.154076, 559, 61, 121],
  ["UNY", "Utica, NY", "Earth Networks, Inc.", 42.878973, 74.785196, 483, 60, 68],
  ["NWK", "Newark, NJ", "Earth Networks, Inc.", 40.734515, 74.165613, 5, 125, 143],
  ["FPL", "Flax Pond, NY", "Earth Networks, Inc.", 40.960864, 73.139142, 8, 13, 20],
]

const gasesBySite = {
  FPL: ["ch4", "co2", "co"],
  NWK: ["ch4", "co2", "co"],
}

const defaultGases = ["ch4", "co2"]

const gasLabel = gas => {
  if (gas === "co2") return <>CO<sub>2</sub></>
  if (gas === "ch4") return <>CH<sub>4</sub></>
  return gas.toUpperCase()
}

const columns = [
  { key: "site", label: "Site ID" },
  { key: "location", label: "Location" },
  { key: "operator", label: "Operator" },
  { key: "lat", label: "Latitude" },
  { key: "lon", label: "Longitude" },
  { key: "elev", label: "Elevation (m.a.s.l.)" },
]

const columnIndex = {
  site: 0,
  location: 1,
  operator: 2,
  lat: 3,
  lon: 4,
  elev: 5,
}

const operatorColors = {
  "Earth Networks, Inc.": "#1f77b4",
}

const GHGDownloads = () => {
  const [sortColumn, setSortColumn] = useState("site")
  const [sortDirection, setSortDirection] = useState("asc")

  const sortedSites = useMemo(() => {
    const sortable = [...sites]

    sortable.sort((a, b) => {
      const valA = a[columnIndex[sortColumn]]
      const valB = b[columnIndex[sortColumn]]

      if (typeof valA === "string") {
        return sortDirection === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }

      return sortDirection === "asc" ? valA - valB : valB - valA
    })

    return sortable
  }, [sortColumn, sortDirection])

  const handleSort = column => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortColumn(column)
      setSortDirection("asc")
    }
  }

  const sortArrow = column => {
    if (sortColumn !== column) return ""
    return sortDirection === "asc" ? " ▲" : " ▼"
  }

  return (
    <section style={{ padding: "2rem", maxWidth: "1600px", margin: "0 auto" }}>
      <h2>Greenhouse Gas Data Downloads</h2>

      <p>
        Following the cancellation of the National Institute for Standards and
        Technology (NIST){" "}
        <a
          href="https://earth.gov/ghgcenter/data-catalog/nec-testbed-ghg-concentrations"
          target="_blank"
          rel="noopener noreferrer"
        >
          Northeast Corridor (NEC) Urban Test Bed Project
        </a>
        , the New York State Energy Research and Development Authority (NYSERDA)
        is funding the continuation of hourly greenhouse gas measurements at nine
        sites in the Northeastern United States in the vicinity of New York State,
        operated by Earth Networks, Inc. Our group is responsible for processing
        and publicly hosting these data following the original NEC framework as
        described by{" "}
        <a
          href="https://doi.org/10.5194/essd-12-699-2020"
          target="_blank"
          rel="noopener noreferrer"
        >
          Karion et al. (2020)
        </a>.
      </p>

      <ul>
        <li>
          Karion, A., Callahan, W., Stock, M., Prinzivalli, S., Verhulst, K. R.,
          Kim, J., Salameh, P. K., Lopez-Coto, I., and Whetstone, J. (2020):
          <em> Greenhouse Gas Observations from the Northeast Corridor Tower Network</em>,
          Earth System Science Data, 12, 699–717, DOI:{" "}
          <a
            href="https://doi.org/10.5194/essd-12-699-2020"
            target="_blank"
            rel="noopener noreferrer"
          >
            10.5194/essd-12-699-2020
          </a>
        </li>
      </ul>

      <p>
        Please report any issues to{" "}
        <a href="mailto:lee.murray@rochester.edu">Lee T. Murray</a>.
      </p>

      <div style={mapWrapper}>
        <LeafletGHGMap sites={sites} operatorColors={operatorColors} />
      </div>

      <div style={legend}>
        {Object.entries(operatorColors).map(([operator, color]) => (
          <span key={operator} style={legendItem}>
            <span style={{ ...legendSwatch, backgroundColor: color }} />
            {operator}
          </span>
        ))}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "1.5rem" }}>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  style={{ ...th, cursor: "pointer", userSelect: "none" }}
                >
                  {col.label}
                  {sortArrow(col.key)}
                </th>
              ))}

              {years.map(year => (
                <th key={year} style={th}>
                  {year} Downloads
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sortedSites.map(([site, location, operator, lat, lon, elev, bot, top]) => {
              const gases = gasesBySite[site] || defaultGases
              const inlets = [bot, top]

              return (
                <tr key={site}>
                  <td style={td}>{site}</td>

                  <td style={td}>
                    <a
                      href={`https://www.google.com/maps?q=${lat},-${Math.abs(lon)}&t=k`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {location}
                    </a>
                  </td>

                  <td style={td}>{operator}</td>
                  <td style={td}>{lat.toFixed(4)}°N</td>
                  <td style={td}>{lon.toFixed(4)}°W</td>
                  <td style={td}>{elev}</td>

                  {years.map(year => (
                    <td key={`${site}-${year}`} style={td}>
                      {inlets.map(inlet => (
                        <div key={`${site}-${year}-${inlet}`} style={downloadGroup}>
                          <strong>{inlet} m.a.g.l.:</strong>{" "}

                          {gases.map((gas, i) => (
                            <span key={`${site}-${year}-${inlet}-${gas}`}>
                              {i > 0 && " | "}
                              <a
                                href={`${dataRoot}/${site}-${year}-${gas}-${inlet}m-1-hour-${version}.csv`}
                                download
                              >
                                {gasLabel(gas)} csv
                              </a>
                              {" / "}
                              <a
                                href={`${dataRoot}/${site}-${year}-${gas}-${inlet}m-1-hour-${version}.nc`}
                                download
                              >
                                nc
                              </a>
                            </span>
                          ))}
                        </div>
                      ))}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

const mapWrapper = {
  height: "500px",
  width: "100%",
  marginTop: "1.5rem",
  marginBottom: "0.75rem",
  border: "1px solid #ddd",
}

const legend = {
  display: "flex",
  flexWrap: "wrap",
  gap: "1rem",
  marginBottom: "1.5rem",
  fontSize: "0.95rem",
}

const legendItem = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
}

const legendSwatch = {
  width: "14px",
  height: "14px",
  borderRadius: "50%",
  display: "inline-block",
}

const downloadGroup = {
  marginBottom: "0.35rem",
}

const th = {
  padding: "0.75rem",
  textAlign: "left",
  borderBottom: "2px solid #333",
  backgroundColor: "#f5f5f5",
  fontWeight: "bold",
  whiteSpace: "nowrap",
}

const td = {
  padding: "0.75rem",
  borderBottom: "1px solid #ddd",
  whiteSpace: "nowrap",
  verticalAlign: "top",
}

export default GHGDownloads